import { createReducer, on, Action } from '@ngrx/store';
import { User, UserListArgs } from 'src/shared/api';
import { EntityState, EntityAdapter, createEntityAdapter } from '@ngrx/entity';
import * as AdminUsersActions from './admin-user.actions';

export const adminUserFeatureKey = 'admin-user';

export interface AdminUsersState extends EntityState<User> {
	error?: any;
	isLoadingList?: boolean;
	isListInitialized?: boolean;
	isLoadingDetail?: boolean;
	shouldStayOnPage?: boolean;
	isDetailInitialized?: boolean;
	isSaving?: boolean;
	selectedAdminUserId?: string;
	listArgs?: UserListArgs;
	results?: User[];
}

export const adminUsersAdapter: EntityAdapter<User> = createEntityAdapter<
	User
>();

export interface AdminUsersPartialState {
	readonly [adminUserFeatureKey]: AdminUsersState;
}

export const initialState: AdminUsersState = adminUsersAdapter.getInitialState({
	isListInitialized: false,
	// additional entity state properties
});

const adminUsersReducer = createReducer(
	initialState,
	on(AdminUsersActions.filtersUpdated, (state, { filters }) => ({
		...state,
		listArgs: filters,
		isLoadingList: true,
	})),
	on(AdminUsersActions.adminUsersLoaded, (state, { users }) =>
		adminUsersAdapter.upsertMany(users, {
			...state,
			results: users,
			isLoadingList: false,
			isListInitialized: true,
		})
	),
	on(AdminUsersActions.adminUserLoadedFromApi, (state, { user }) =>
		adminUsersAdapter.upsertOne(user, { ...state, isLoadingDetail: false })
	),
	on(AdminUsersActions.adminUserLoadedFromStore, (state) => ({
		...state,
		isLoadingDetail: false,
	})),
	on(AdminUsersActions.saveRequested, (state, { shouldStayOnPage }) => ({
		...state,
		isSaving: true,
		shouldStayOnPage,
	})),
	on(
		AdminUsersActions.adminUserSelected,
		(state, { selectedAdminUserId }) => ({
			...state,
			isLoadingDetail: true,
			selectedAdminUserId,
		})
	),
	on(AdminUsersActions.errorSavingAdminUser, (state, { error }) => ({
		...state,
		error: error,
		isSaving: false,
	})),
	on(AdminUsersActions.adminUserSaved, (state, { user }) =>
		adminUsersAdapter.updateOne(
			{
				id: user.id,
				changes: {
					...user,
				},
			},
			{ ...state, isSaving: false }
		)
	),
	on(AdminUsersActions.errorLoadingAdminUsers, (state, { error }) => ({
		...state,
		error,
		isLoadingList: false,
		isListInitialized: true,
	})),
	on(AdminUsersActions.adminUserDetailDestroyed, (state) => ({
		...state,
		isDetailInitialized: false,
	})),
	on(AdminUsersActions.adminUserListDestroyed, (state) => ({
		...state,
		isInitialized: false,
		selectedAdminUserId: undefined,
		shouldStayOnPage: false,
	}))
);

export function reducer(state: AdminUsersState | undefined, action: Action) {
	return adminUsersReducer(state, action);
}
